import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cookie, Check, Settings, AlertCircle } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const COOKIE_CONSENT_KEY = 'sweetpilot_cookie_consent';

const cookieCategories = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    description:
      'These cookies are necessary for the website to function and cannot be disabled. They are usually set in response to your actions like logging in or filling forms.',
    required: true,
    cookies: [
      { name: 'session_id', purpose: 'Maintains your logged-in session', duration: 'Session' },
      { name: 'csrf_token', purpose: 'Protects against cross-site request forgery', duration: 'Session' },
      { name: 'cookie_consent', purpose: 'Stores your cookie preferences', duration: '1 year' },
    ],
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    description:
      'These cookies enable personalized features like remembering your preferences, language settings, and customizations.',
    required: false,
    cookies: [
      { name: 'theme_preference', purpose: 'Remembers your dark/light mode preference', duration: '1 year' },
      { name: 'language', purpose: 'Stores your language preference', duration: '1 year' },
      { name: 'sidebar_state', purpose: 'Remembers sidebar collapsed/expanded state', duration: '1 year' },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    description:
      'These cookies help us understand how visitors interact with our website by collecting anonymous information.',
    required: false,
    cookies: [
      { name: '_ga', purpose: 'Google Analytics - distinguishes users', duration: '2 years' },
      { name: '_gid', purpose: 'Google Analytics - identifies session', duration: '24 hours' },
      { name: 'mixpanel_id', purpose: 'Mixpanel analytics tracking', duration: '1 year' },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    description:
      'These cookies are used to show you relevant ads and measure the effectiveness of our advertising campaigns.',
    required: false,
    cookies: [
      { name: '_fbp', purpose: 'Facebook Pixel - tracks conversions', duration: '3 months' },
      { name: 'li_sugr', purpose: 'LinkedIn ads tracking', duration: '3 months' },
      { name: 'gclid', purpose: 'Google Ads conversion tracking', duration: '90 days' },
    ],
  },
];

interface CookiePreferences {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function Cookies() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    functional: true,
    analytics: true,
    marketing: false,
  });
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPreferences, setOriginalPreferences] = useState<CookiePreferences | null>(null);

  // Load saved preferences on mount
  useEffect(() => {
    try {
      const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (savedConsent) {
        const parsed = JSON.parse(savedConsent) as CookiePreferences;
        setPreferences(parsed);
        setOriginalPreferences(parsed);
      } else {
        setOriginalPreferences(preferences);
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Track changes
  useEffect(() => {
    if (originalPreferences) {
      const changed = Object.keys(preferences).some(
        key => preferences[key as keyof CookiePreferences] !== originalPreferences[key as keyof CookiePreferences]
      );
      setHasChanges(changed);
    }
  }, [preferences, originalPreferences]);

  const togglePreference = (id: string) => {
    if (id === 'essential') return; // Can't disable essential
    setPreferences((prev) => ({ ...prev, [id]: !prev[id as keyof typeof prev] }));
    setSaved(false);
  };

  const savePreferences = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
      setOriginalPreferences(preferences);
      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // Handle storage errors silently
    }
  };

  const acceptAll = () => {
    const allEnabled: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allEnabled);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allEnabled));
    setOriginalPreferences(allEnabled);
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const rejectNonEssential = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(essentialOnly));
    setOriginalPreferences(essentialOnly);
    setSaved(true);
    setHasChanges(false);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <StaticPageLayout
      title="Cookie Policy"
      subtitle="Last updated: December 1, 2024"
    >
      {/* Introduction */}
      <section className="mb-12">
        <div className={`p-8 rounded-2xl border ${
          isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
              isDark ? 'bg-amber-500/10' : 'bg-amber-100'
            }`}>
              <Cookie className={`w-6 h-6 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>What Are Cookies?</h2>
              <p className={isDark ? 'text-gray-300' : 'text-slate-600'}>
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, keeping you logged in, and understanding how you use our service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Preferences */}
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Manage Cookie Preferences</h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={rejectNonEssential}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium ${
                isDark
                  ? 'bg-dark-700 text-gray-300 hover:bg-dark-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Essential Only
            </button>
            <button
              onClick={acceptAll}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium ${
                isDark
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              Accept All
            </button>
            <button
              onClick={savePreferences}
              disabled={!hasChanges}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium ${
                hasChanges
                  ? isDark
                    ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  : isDark
                    ? 'bg-dark-700 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Settings className="w-4 h-4" />
              Save Preferences
            </button>
          </div>
        </div>

        {/* Success Message */}
        {saved && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`flex items-center gap-2 p-4 rounded-xl mb-6 ${
              isDark ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-green-50 border border-green-200 text-green-700'
            }`}
          >
            <Check className="w-5 h-5" />
            <span>Your cookie preferences have been saved successfully!</span>
          </motion.div>
        )}

        <div className="space-y-6">
          {cookieCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-2xl border overflow-hidden ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-white/5' : 'border-slate-100'
              }`}>
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{category.name}</h3>
                    {category.required && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
                      }`}>
                        Required
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{category.description}</p>
                </div>
                <button
                  onClick={() => togglePreference(category.id)}
                  disabled={category.required}
                  className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${
                    preferences[category.id as keyof typeof preferences]
                      ? isDark ? 'bg-green-500' : 'bg-green-500'
                      : isDark ? 'bg-dark-600' : 'bg-slate-300'
                  } ${category.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                      preferences[category.id as keyof typeof preferences]
                        ? 'translate-x-8'
                        : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Cookie List */}
              <div className="p-6 overflow-x-auto">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className={`text-left text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      <th className="pb-3 font-medium">Cookie Name</th>
                      <th className="pb-3 font-medium">Purpose</th>
                      <th className="pb-3 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                    {category.cookies.map((cookie) => (
                      <tr key={cookie.name} className="text-sm">
                        <td className={`py-3 font-mono ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{cookie.name}</td>
                        <td className={`py-3 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{cookie.purpose}</td>
                        <td className={`py-3 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{cookie.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How to Control Cookies */}
      <section className="mb-12">
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>How to Control Cookies</h2>
        <div className="max-w-none">
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
            You can control and delete cookies through your browser settings. Here's how to manage cookies in popular browsers:
          </p>
          <ul className="space-y-3">
            {[
              { browser: 'Chrome', url: 'https://support.google.com/chrome/answer/95647' },
              { browser: 'Firefox', url: 'https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer' },
              { browser: 'Safari', url: 'https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac' },
              { browser: 'Edge', url: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09' },
            ].map((item) => (
              <li key={item.browser} className={isDark ? 'text-gray-300' : 'text-slate-600'}>
                <strong className={isDark ? 'text-white' : 'text-slate-900'}>{item.browser}:</strong>{' '}
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
                >
                  Cookie settings guide
                </a>
              </li>
            ))}
          </ul>
          <div className={`flex items-start gap-3 mt-6 p-4 rounded-xl ${
            isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'
          }`}>
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            <p className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
              Disabling certain cookies may affect the functionality of our website and limit your access to some features.
            </p>
          </div>
        </div>
      </section>

      {/* Third-Party Cookies */}
      <section className="mb-12">
        <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>Third-Party Cookies</h2>
        <div className={`p-6 rounded-2xl border ${
          isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
        }`}>
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
            We use cookies from the following third-party services:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { name: 'Google Analytics', purpose: 'Website analytics and performance monitoring' },
              { name: 'Mixpanel', purpose: 'Product analytics and user behavior tracking' },
              { name: 'Stripe', purpose: 'Payment processing and fraud prevention' },
              { name: 'Intercom', purpose: 'Customer support chat widget' },
            ].map((service) => (
              <div key={service.name} className={`flex items-start gap-3 p-4 rounded-xl ${
                isDark ? 'bg-dark-700/50' : 'bg-slate-50'
              }`}>
                <Check className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                <div>
                  <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{service.name}</h4>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{service.purpose}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Updates */}
      <section className="mb-12">
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Updates to This Policy</h2>
        <p className={isDark ? 'text-gray-300' : 'text-slate-600'}>
          We may update this Cookie Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
        </p>
      </section>

      {/* Contact */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Questions About Cookies?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          If you have any questions about our use of cookies, please contact our privacy team.
        </p>
        <a
          href="mailto:privacy@sweetpilot.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Contact Us
        </a>
      </section>
    </StaticPageLayout>
  );
}
